export const DEFAULT_ASPECT_RATIOS = [
    { id: 'ar-1-1', ratio: '1:1', name: '' },
    { id: 'ar-16-9', ratio: '16:9', name: '' },
    { id: 'ar-9-16', ratio: '9:16', name: '' },
    { id: 'ar-4-3', ratio: '4:3', name: '' },
    { id: 'ar-3-4', ratio: '3:4', name: '' },
];

export function createAspectId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `ar-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function normalizeAspectRatio(value) {
    const text = String(value || '').trim();
    const match = text.match(/^(\d{1,4})\s*[:x×/]\s*(\d{1,4})$/i);
    if (!match) return null;
    const w = Number(match[1]);
    const h = Number(match[2]);
    if (!w || !h) return null;
    return `${w}:${h}`;
}

/** Приводит элемент к { id, ratio, name }, поддерживает старый формат строки */
export function normalizeAspectItem(item, { generateId = true } = {}) {
    if (typeof item === 'string') {
        const ratio = normalizeAspectRatio(item);
        if (!ratio) return null;
        return {
            id: generateId ? createAspectId() : '',
            ratio,
            name: '',
        };
    }
    if (!item || typeof item !== 'object') return null;
    const ratio = normalizeAspectRatio(item.ratio || item.value || item.aspect_ratio);
    if (!ratio) return null;
    const existingId = String(item.id || '').trim();
    return {
        id: existingId || (generateId ? createAspectId() : ''),
        ratio,
        name: String(item.name || item.label || '').trim(),
    };
}

export function normalizeAspectList(list) {
    if (!Array.isArray(list) || !list.length) {
        return DEFAULT_ASPECT_RATIOS.map((item) => ({ ...item }));
    }
    const cleaned = [];
    const seenIds = new Set();
    for (const item of list) {
        const parsed = normalizeAspectItem(item);
        if (!parsed) continue;
        // id должен быть уникален; одинаковые ratio разрешены
        let id = parsed.id;
        if (!id || seenIds.has(id)) {
            id = createAspectId();
        }
        seenIds.add(id);
        cleaned.push({ ...parsed, id });
    }
    return cleaned.length ? cleaned : DEFAULT_ASPECT_RATIOS.map((item) => ({ ...item }));
}

export function aspectOptionLabel(item) {
    const parsed = normalizeAspectItem(item, { generateId: false });
    if (!parsed) return '';
    return parsed.name || parsed.ratio;
}

/** Размер мини-превью рамки для ratio (вписывается в квадрат max×max) */
export function aspectPreviewSize(ratio, max = 16) {
    const normalized = normalizeAspectRatio(ratio);
    if (!normalized) return { width: max, height: max };
    const [w, h] = normalized.split(':').map(Number);
    if (!w || !h) return { width: max, height: max };
    if (w >= h) {
        return { width: max, height: Math.max(4, Math.round((max * h) / w)) };
    }
    return { width: Math.max(4, Math.round((max * w) / h)), height: max };
}


export function aspectsEqual(a, b) {
    const left = normalizeAspectList(a);
    const right = normalizeAspectList(b);
    if (left.length !== right.length) return false;
    return left.every(
        (item, i) =>
            item.id === right[i].id &&
            item.ratio === right[i].ratio &&
            item.name === right[i].name
    );
}

/** Значения aspect_ratio из supported_parameters модели (null = модель не ограничивает) */
export function getModelAspectValues(params) {
    const ar = params?.aspect_ratio;
    if (!ar) return null;
    const raw = Array.isArray(ar.values) ? ar.values : Array.isArray(ar) ? ar : null;
    if (!raw) return null;
    const values = [];
    const seen = new Set();
    for (const item of raw) {
        const ratio = normalizeAspectRatio(item);
        if (!ratio || seen.has(ratio)) continue;
        seen.add(ratio);
        values.push(ratio);
    }
    return values;
}

/**
 * Бейджи aspect_ratio для списка моделей:
 * если пользователь задал имя для соотношения и модель его поддерживает — показываем имя вместо "1:1".
 */
export function resolveAspectBadgeItems(modelValues, userItems) {
    if (!Array.isArray(modelValues) || !modelValues.length) return [];
    const nameByRatio = new Map();
    for (const item of normalizeAspectList(userItems)) {
        const name = String(item.name || '').trim();
        if (!name) continue;
        if (!nameByRatio.has(item.ratio)) nameByRatio.set(item.ratio, name);
    }
    return modelValues.map((ratio) => ({
        value: ratio,
        label: nameByRatio.get(ratio) || ratio,
    }));
}

/**
 * Список опций формата:
 * 1) пользовательские aspect_ratios сверху (с флагом available, если есть в модели)
 * 2) остальные значения модели
 * Если у модели нет aspect_ratio.values — только пользовательский список.
 */
export function buildAspectSelectOptions({ userItems, modelValues }) {
    const users = normalizeAspectList(userItems);
    const constrained = Array.isArray(modelValues) && modelValues.length > 0;
    const modelSet = constrained ? new Set(modelValues) : null;
    const covered = new Set();
    const options = [];

    for (const item of users) {
        const available = constrained ? modelSet.has(item.ratio) : null;
        covered.add(item.ratio);
        options.push({
            value: item.id,
            ratio: item.ratio,
            name: item.name,
            source: 'user',
            available,
            disabled: constrained && !available,
            label: aspectOptionLabel(item),
        });
    }

    if (constrained) {
        for (const ratio of modelValues) {
            if (covered.has(ratio)) continue;
            covered.add(ratio);
            options.push({
                value: `model:${ratio}`,
                ratio,
                name: '',
                source: 'model',
                available: true,
                disabled: false,
                label: ratio,
            });
        }
    }

    return options;
}
